import { AsyncPipe, formatDate } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  inject,
  OnInit,
  signal,
  ViewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule, ReactiveFormsModule, UntypedFormControl } from '@angular/forms';
import {
  NgOptionComponent,
  NgOptionTemplateDirective,
  NgSelectComponent,
} from '@ng-select/ng-select';
import dayjs from 'dayjs';
import { BehaviorSubject, combineLatest, firstValueFrom } from 'rxjs';
import { map, take } from 'rxjs/operators';

import { BudgetQuery } from '@models/budget/budget.query';

import { FileManagerComponent } from '@shared/components/file-manager/file-manager.component';
import { FileViewerComponent } from '@shared/components/file-viewer/file-viewer.component';
import { IconComponent } from '@shared/components/icon/icon.component';
import { CustomOverlayRef } from '@shared/components/overlay/custom-overlay-ref';
import { MessagesConstants } from '@shared/constants/messages.constants';
import { TooltipDirective } from '@shared/directives/tooltip.directive';
import { VarDirective } from '@shared/directives/var.directive';
import { ApiService, FileMetadata } from '@shared/services/api.service';
import {
  BudgetType,
  EntityType,
  EventType,
  GqlService,
  TemplateType,
  WorkflowStep,
} from '@shared/services/gql.service';
import { LfuService } from '@shared/services/lfu.service';
import { OverlayService } from '@shared/services/overlay.service';
import { MainQuery } from '@shared/store/main/main.query';
import { OrganizationQuery } from '@shared/store/organization/organization.query';
import { WorkflowQuery } from '@shared/store/workflow/workflow.query';
import { removeSpecialSymbolsFromFileName } from '@shared/utils/formatter.utils';
import { Utils } from '@shared/utils/utils';

interface MonthOption {
  date: string;
  disabled?: boolean;
  name: string;
}

Utils.extendDayjs();

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    IconComponent,
    VarDirective,
    NgSelectComponent,
    FormsModule,
    ReactiveFormsModule,
    NgOptionTemplateDirective,
    TooltipDirective,
    NgOptionComponent,
    FileManagerComponent,
    FileViewerComponent,
    AsyncPipe,
  ],
  selector: 'aux-quarter-close-upload',
  styleUrl: 'vendor-estimate-upload.component.scss',
  templateUrl: 'vendor-estimate-upload.component.html',
})
export class VendorEstimateUploadComponent implements OnInit {
  ref = inject(
    CustomOverlayRef<
      unknown,
      {
        isSelectedMonthOpen?: boolean;
        onSuccess: (processEventId: string) => void;
        selectedMonth?: null | string;
        selectedVendorIDs?: string[];
      }
    >
  );
  vendorsQuery = inject(OrganizationQuery);

  private apiService = inject(ApiService);
  private budgetQuery = inject(BudgetQuery);
  private destroyRef = inject(DestroyRef);
  private gqlService = inject(GqlService);
  private lfuService = inject(LfuService);
  private mainQuery = inject(MainQuery);
  private overlayService = inject(OverlayService);
  private workflowQuery = inject(WorkflowQuery);

  errorMessage = signal('');

  availableMonths = this.getAvailableMonths();
  currentMonth$ = this.mainQuery
    .select('currentOpenMonth')
    .pipe(map((m) => dayjs(m).format('MM-DD-YYYY')));
  @ViewChild(FileManagerComponent) fileManager: FileManagerComponent | undefined;
  isConfirmVendorExpensesLocked!: boolean;
  isReviewVendorEstimatesLocked!: boolean;
  loading$ = new BehaviorSubject(false);
  readonly MessagesConstants = MessagesConstants;
  metadata: FileMetadata = {};
  selectedMonth = new UntypedFormControl('');
  selectedVendor = new UntypedFormControl('');
  templateLoading$ = new BehaviorSubject(false);

  ngOnInit(): void {
    combineLatest([
      this.workflowQuery.getLockStatusByWorkflowStepType$(
        WorkflowStep.WF_STEP_MONTH_CLOSE_LOCK_ADJUSTMENTS
      ),
      this.workflowQuery.getLockStatusByWorkflowStepType$(
        WorkflowStep.WF_STEP_MONTH_CLOSE_LOCK_VENDOR_ESTIMATES
      ),
    ])
      .pipe(take(1), takeUntilDestroyed(this.destroyRef))
      .subscribe(
        ([isConfirmVendorExpensesLocked, isReviewVendorEstimatesLocked]: [boolean, boolean]) => {
          this.isConfirmVendorExpensesLocked = isConfirmVendorExpensesLocked;
          this.isReviewVendorEstimatesLocked = isReviewVendorEstimatesLocked;

          if (
            (!isConfirmVendorExpensesLocked && !isReviewVendorEstimatesLocked) ||
            !this.ref.data?.isSelectedMonthOpen
          ) {
            if (this.ref.data.selectedMonth) {
              const date = dayjs(this.ref.data.selectedMonth).format('MM-01-YYYY');

              if (this.availableMonths.some((month) => month.date === date)) {
                this.selectedMonth.reset(date);
              }
            }

            if (this.availableMonths.length === 1) {
              this.selectedMonth.reset(this.availableMonths[0].date);
            }
          }

          if (!!this.ref.data.selectedVendorIDs && this.ref.data.selectedVendorIDs.length === 1) {
            this.selectedVendor.reset(this.ref.data.selectedVendorIDs[0]);
          }
        }
      );
  }

  async downloadVendorEstimateTemplate() {
    if (!this.selectedMonth.value) {
      this.overlayService.error('Please select a month');

      return;
    }

    if (!this.selectedVendor.value) {
      this.overlayService.error('Please select a Vendor Contract');

      return;
    }

    if (!this.templateLoading$.getValue()) {
      this.templateLoading$.next(true);
      const month = dayjs(this.selectedMonth.value, 'MM-DD-YYYY');
      const { data, errors, success } = await this.lfuService.getTemplatePath({
        payload: JSON.stringify({ month: month.format('MMM-YYYY').toUpperCase() }),
        template_type: TemplateType.VENDOR_ESTIMATE_TEMPLATE,
        vendor_id: this.selectedVendor.value,
      });

      if (!success) {
        this.overlayService.error(errors);
      }

      if (!(success && data)) {
        this.overlayService.error('There was a problem downloading the template');
      } else {
        const vendorName = removeSpecialSymbolsFromFileName(
          this.vendorsQuery.getEntity(this.selectedVendor.value)?.name
        );
        const fileName = `${vendorName}_${month.format('MMMM')}_${month.year()}_${TemplateType.VENDOR_ESTIMATE_TEMPLATE}`;

        await this.apiService.downloadFileFromPath(data.id, fileName);
      }

      this.templateLoading$.next(false);
    }
  }

  // Revisit after month reopen, as current month may no longer be enough information
  getAvailableMonths() {
    const { budget_info } = this.budgetQuery.getValue();
    let date = dayjs(budget_info[0].current_month);
    const months: MonthOption[] = [];
    const current_quarter = date.quarter();

    while (date.quarter() === current_quarter) {
      months.push({ date: date.format('MM-DD-YYYY'), name: date.format('MMMM YYYY') });
      date = date.add(1, 'months');
    }

    return months;
  }

  getFilePath() {
    const vendorId = this.selectedVendor.value;
    // const date = formatDate(new Date(), 'MMM-YYYY', 'en-US');
    const { budget_info } = this.budgetQuery.getValue();
    const date = budget_info[0].current_month;

    if (!date) {
      this.errorMessage.set('No in month found!');

      return '';
    }

    const formatted_current_month = formatDate(date, 'MMMM-y', 'en-US');
    const trialId = this.mainQuery.getValue().trialKey;

    // vendor/<vendor uuid>/in-month/<MON-YYYY>/<file name goes here>
    return `trials/${trialId}/vendors/${vendorId}/vendor-estimate/${formatted_current_month}/`;
  }

  onDisabledOptionClick(event: MouseEvent): boolean {
    event.stopPropagation();

    return false;
  }

  async onUpload() {
    this.errorMessage.set('');

    if (this.fileManager && !this.loading$.getValue()) {
      const files = this.fileManager.fileQuery.getAll();

      if (!files.length) {
        this.errorMessage.set(MessagesConstants.FILE.NEED_UPLOAD_FILE);

        return;
      }

      if (files.length > 1) {
        this.errorMessage.set(MessagesConstants.FILE.MAX_ONE_FILE);

        return;
      }

      if (!this.selectedMonth.value) {
        this.overlayService.error('Please select a month');

        return;
      }

      if (!this.selectedVendor.value) {
        this.overlayService.error('Please select a vendor');

        return;
      }

      this.loading$.next(true);

      const file = files[0];
      const key = `${this.getFilePath()}${file.bucket_key}`;

      this.fileManager.fileStore.update(file.id, {
        ...file,
        bucket_key: key,
      });

      const fileSuccess = await this.fileManager.fileService.uploadFiles({
        metadata: { admin: '1' },
      });

      if (fileSuccess) {
        const { data, errors, success } = await firstValueFrom(
          this.gqlService.validateEventFile$({
            bucket_key: `public/${key}`,
            entity_id: this.selectedVendor.value,
            entity_type: EntityType.ORGANIZATION,
            payload: JSON.stringify({
              budget_type: BudgetType.BUDGET_VENDOR_ESTIMATE,
              period: this.selectedMonth.value,
            }),
            type: EventType.VENDOR_ESTIMATE_TEMPLATE_UPLOADED,
          })
        );

        if (success && data?.id) {
          this.overlayService.success();
          this.ref.data?.onSuccess(data.id);
        } else {
          void this.apiService.removeFile(`${this.getFilePath()}${file.bucket_key}`);
          this.overlayService.error(errors, undefined, true);
        }

        this.ref.close(true);
      }
    }

    this.loading$.next(false);
  }

  pathFn: () => string = () => '';

  get workflowLocked(): boolean {
    return this.isConfirmVendorExpensesLocked || this.isReviewVendorEstimatesLocked;
  }
}
